// src/shared/components/ui/MinimalisticTable.tsx
import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import DarkMinimalFilter from './DarkMinimalFilter';
import DarkDataTable, { type DarkDataTableColumn } from './DarkDataTable';

export interface ColumnDef<RowType> {
  header: React.ReactNode;
  accessor: keyof RowType | '';
  Cell?: (value: any, row: RowType) => React.ReactNode;
  /** Set false to opt-out of sorting for this column. Defaults to true for non-empty accessors. */
  sortable?: boolean;
}

export interface MinimalisticTableProps<RowType> {
  columns: ColumnDef<RowType>[];
  data: RowType[];
  noDataMessage?: string;

  showNewButton?: boolean;
  onNew?: () => void;

  editableAccessors?: Array<keyof RowType>;
  newRowValues?: Partial<Record<keyof RowType, any>>;
  onNewRowChange?: (accessor: keyof RowType, val: any) => void;
  onAcceptNew?: () => void;
  onCancelNew?: () => void;

  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Called when user presses Enter in the search box */
  onSearchEnter?: () => void;

  /** Fired when a non-new row is clicked */
  onRowClick?: (row: RowType) => void;
  /** Return true if that row should respond to clicks */
  isRowClickable?: (row: RowType) => boolean;
}

export default function MinimalisticTable<RowType>({
  columns,
  data,
  noDataMessage = 'No data found.',
  showNewButton = true,
  onNew,
  editableAccessors,
  newRowValues,
  onNewRowChange,
  onAcceptNew,
  onCancelNew,

  showSearch = true,
  searchPlaceholder = 'Search…',
  onSearchEnter,

  onRowClick,
  isRowClickable,
}: MinimalisticTableProps<RowType>) {
  // ─── Search State ────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  // ─── Sort State ──────────────────────────────────────────────────────
  const [sortKey, setSortKey] = React.useState<keyof RowType | ''>('');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  const handleSortClick = React.useCallback((accessor: keyof RowType | '') => {
    if (!accessor) return;
    if (sortKey !== accessor) {
      // new column → start asc
      setSortKey(accessor);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      // same column, asc → desc
      setSortDir('desc');
    } else {
      // same column, desc → clear
      setSortKey('');
      setSortDir('asc');
    }
  }, [sortKey, sortDir]);

  // ─── Filtered Data ────────────────────────────────────────────────────
  const filteredData: RowType[] = React.useMemo(() => {
    if (!searchTerm) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        if (!col.accessor) return false;
        const cellValue = (row[col.accessor] as any);
        if (cellValue == null) return false;
        return String(cellValue).toLowerCase().includes(lower);
      })
    );
  }, [data, searchTerm, columns]);

  // ─── Sorted Data ─────────────────────────────────────────────────────
  const sortedData: RowType[] = React.useMemo(() => {
    if (!sortKey) return filteredData;
    return [...filteredData].sort((a, b) => {
      const av = (a as any)[sortKey as string];
      const bv = (b as any)[sortKey as string];
      if (av == null && bv == null) return 0;
      if (av == null) return sortDir === 'asc' ? 1 : -1;
      if (bv == null) return sortDir === 'asc' ? -1 : 1;
      if (typeof av === 'boolean') {
        if (av === bv) return 0;
        return sortDir === 'asc' ? (av ? 1 : -1) : (av ? -1 : 1);
      }
      const cmp = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortKey, sortDir]);

  const darkColumns: Array<DarkDataTableColumn<RowType>> = columns.map((col, idx) => {
    const isLast = idx === columns.length - 1;
    const shouldShowButton = isLast && showNewButton && !!onNew;
    const canSort = !!col.accessor && col.sortable !== false;
    const isSortActive = canSort && sortKey === col.accessor;

    // ── Sort-aware header node ──────────────────────────────────────────
    const sortableHeaderContent = canSort ? (
      <button
        type="button"
        className="group inline-flex items-center gap-1 select-none text-left focus:outline-none"
        onClick={() => handleSortClick(col.accessor)}
        title={
          isSortActive
            ? sortDir === 'asc'
              ? 'Sorted ascending – click to reverse'
              : 'Sorted descending – click to clear'
            : `Sort by ${String(col.header)}`
        }
      >
        <span
          className={
            isSortActive
              ? 'text-neutral-200 font-medium'
              : 'text-neutral-300 group-hover:text-neutral-500 transition-colors'
          }
        >
          {col.header}
        </span>
        <span
          className={
            isSortActive
              ? 'text-neutral-400'
              : 'text-neutral-600 group-hover:text-neutral-800 transition-colors'
          }
        >
          {isSortActive ? (
            sortDir === 'asc' ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3" />
          )}
        </span>
      </button>
    ) : (
      col.header
    );

    const header = isLast ? (
      <div className="flex items-center justify-between gap-2">
        {sortableHeaderContent}
        <div className="flex items-center gap-2">
          {showSearch && (
            <div className="w-48">
              <DarkMinimalFilter
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={searchPlaceholder}
                onEnter={onSearchEnter}
                size="sm"
              />
            </div>
          )}
          {shouldShowButton && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNew?.();
              }}
              className="bg-transparent text-emerald-500 px-2 py-1 hover:text-emerald-400"
            >
              New
            </button>
          )}
        </div>
      </div>
    ) : (
      sortableHeaderContent
    );

    return {
      header,
      // Subtle gray underline on the active sort column.
      thClassName: isSortActive
        ? 'border-b border-neutral-600 !text-neutral-200'
        : undefined,
      render: (row) => {
        const isNewRow = (row as any).__isNew === true;
        const rawValue = col.accessor ? (row[col.accessor] as any) : undefined;

        if (isNewRow) {
          if (editableAccessors && col.accessor && editableAccessors.includes(col.accessor)) {
            return (
              <input
                type="text"
                value={newRowValues?.[col.accessor] ?? ''}
                onChange={(e) => onNewRowChange?.(col.accessor as any, e.target.value)}
                className="w-full pac-glass-input text-neutral-100 px-2 py-1 rounded"
                onClick={(e) => e.stopPropagation()}
              />
            );
          }

          if (idx === columns.length - 1) {
            return (
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAcceptNew?.();
                  }}
                  className="text-emerald-500 hover:text-emerald-400"
                  disabled={!onNewRowChange}
                >
                  Accept
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelNew?.();
                  }}
                  className="text-rose-500 hover:text-rose-400"
                >
                  Cancel
                </button>
              </div>
            );
          }

          return '—';
        }

        return col.Cell ? col.Cell(rawValue, row) : rawValue;
      },
    };
  });

  return (
    <DarkDataTable<RowType>
      columns={darkColumns}
      rows={sortedData}
      emptyMessage={noDataMessage}
      getRowKey={(_row, idx) => String(idx)}
      onRowClick={onRowClick}
      isRowClickable={(row) => {
        if ((row as any).__isNew === true) return false;
        return isRowClickable ? isRowClickable(row) : true;
      }}
    />
  );
}
